import React, { useMemo, useState, useEffect } from 'react';
import { Box, Card, Grid, Typography, Divider } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Bar, Doughnut, Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  Filler,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';

import PhoneInTalkIcon from '@mui/icons-material/PhoneInTalk';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import PhoneMissedIcon from '@mui/icons-material/PhoneMissed';
import PercentIcon from '@mui/icons-material/Percent';
import TimerIcon from '@mui/icons-material/Timer';
import HeadsetMicIcon from '@mui/icons-material/HeadsetMic';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PeopleIcon from '@mui/icons-material/People';

import FilterBarDetalleLlamadas, { aplicarFiltrosDetalle } from './FilterBarDetalleLlamadas';
import { procesarDatosDetalleLlamadas, fmtSeg } from '../helpers/procesarDetalleLlamadas';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  ChartTooltip,
  Legend,
  Filler,
  ChartDataLabels,
);

/* ---- Estilos reutilizables ---- */
const chartBox = (dk) => ({
  border: dk ? '1px solid #3d4a62' : '1px solid #e0e0e0',
  borderRadius: '12px',
  boxShadow: dk ? '0 2px 8px rgba(0,0,0,0.2)' : '0 2px 8px rgba(0,0,0,0.04)',
  p: 2,
  background: dk ? 'rgba(31,42,64,0.5)' : 'rgba(255,255,255,0.5)',
});

const ttStyle = (dk) => ({
  backgroundColor: dk ? '#2d3a52' : '#fff',
  titleColor: dk ? '#e0e0e0' : '#2c3e50',
  bodyColor: dk ? '#b0bec5' : '#5f6368',
  borderColor: dk ? '#3d4a62' : '#e0e0e0',
  borderWidth: 1,
  padding: 12,
  displayColors: true,
});

const gridColor = (dk) => (dk ? '#3d4a62' : '#e0e0e0');
const tickColor = (dk) => (dk ? '#b0bec5' : '#7f8c8d');
const titleColor = (dk) => (dk ? '#e0e0e0' : '#2c3e50');

const SectionTitle = ({ children, isDarkMode }) => (
  <Typography sx={{ color: titleColor(isDarkMode), fontWeight: 700, fontSize: 14, mb: 1 }}>
    {children}
  </Typography>
);

/* ========================================================================= */
/*  KPI Card                                                                 */
/* ========================================================================= */

const KpiCard = ({ title, value, subtitle, icon: Icon, color, isDarkMode }) => (
  <Card
    variant="outlined"
    sx={{
      p: 2,
      borderRadius: 2,
      bgcolor: isDarkMode ? 'rgba(31,42,64,0.5)' : 'rgba(255,255,255,0.9)',
      borderColor: isDarkMode ? '#3d4a62' : '#e0e0e0',
      display: 'flex',
      alignItems: 'center',
      gap: 1.5,
      minHeight: 80,
    }}
  >
    <Box
      sx={{
        width: 44,
        height: 44,
        borderRadius: '12px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: color + '20',
      }}
    >
      <Icon sx={{ color, fontSize: 24 }} />
    </Box>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', display: 'block', lineHeight: 1.2 }}>
        {title}
      </Typography>
      <Typography variant="h6" fontWeight={700} sx={{ fontSize: '1.15rem', lineHeight: 1.3 }}>
        {value}
      </Typography>
      {subtitle && (
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
          {subtitle}
        </Typography>
      )}
    </Box>
  </Card>
);

/* ========================================================================= */
/*  CHARTS                                                                   */
/* ========================================================================= */

const PALETTE = ['#3b82f6', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#f97316', '#10b981', '#6366f1', '#14b8a6', '#a855f7', '#e11d48', '#0ea5e9', '#84cc16'];

/** Interacciones por hora del día – Bar */
const ChartPorHora = ({ data, isDarkMode: dk }) => {
  if (!data) return null;
  const cfg = {
    labels: data.labels,
    datasets: [
      {
        label: 'Interacciones',
        data: data.data,
        backgroundColor: data.data.map((v) => (v > 0 ? '#3b82f6' : '#94a3b8')),
        borderRadius: 3,
      },
    ],
  };
  return (
    <Box sx={{ height: 260, ...chartBox(dk) }}>
      <Bar
        data={cfg}
        options={{
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: false },
            datalabels: { display: false },
            tooltip: ttStyle(dk),
          },
          scales: {
            x: { ticks: { color: tickColor(dk), font: { size: 10 } }, grid: { display: false } },
            y: { beginAtZero: true, ticks: { color: tickColor(dk), precision: 0 }, grid: { color: gridColor(dk) } },
          },
        }}
      />
    </Box>
  );
};

/** Interacciones por fecha – Grouped Bar */
const ChartPorFecha = ({ data, isDarkMode: dk }) => {
  if (!data || !data.labels || data.labels.length === 0) return null;
  const cfg = {
    labels: data.labels,
    datasets: [
      { label: 'Total', data: data.total, backgroundColor: '#3b82f6', borderRadius: 3 },
      { label: 'Atendidas', data: data.atendidas, backgroundColor: '#22c55e', borderRadius: 3 },
      { label: 'Abandonadas', data: data.abandonadas, backgroundColor: '#ef4444', borderRadius: 3 },
    ],
  };
  return (
    <Box sx={{ height: 280, ...chartBox(dk) }}>
      <Bar
        data={cfg}
        options={{
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { position: 'top', labels: { color: tickColor(dk), boxWidth: 12, font: { size: 11 } } },
            datalabels: { display: false },
            tooltip: ttStyle(dk),
          },
          scales: {
            x: { ticks: { color: tickColor(dk), font: { size: 11 } }, grid: { display: false } },
            y: { beginAtZero: true, ticks: { color: tickColor(dk), precision: 0 }, grid: { color: gridColor(dk) } },
          },
        }}
      />
    </Box>
  );
};

/** Duración promedio por fecha – Line */
const ChartDuracionPorFecha = ({ data, isDarkMode: dk }) => {
  if (!data || !data.labels || data.labels.length === 0) return null;
  const cfg = {
    labels: data.labels,
    datasets: [
      {
        label: 'Dur. Prom. (seg)',
        data: data.promDuracion,
        borderColor: '#ec4899',
        backgroundColor: 'rgba(236,72,153,0.1)',
        fill: true,
        tension: 0.3,
        pointRadius: 3,
      },
    ],
  };
  return (
    <Box sx={{ height: 260, ...chartBox(dk) }}>
      <Line
        data={cfg}
        options={{
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: false },
            datalabels: { display: false },
            tooltip: {
              ...ttStyle(dk),
              callbacks: { label: (ctx) => `Duración prom: ${fmtSeg(ctx.raw)}` },
            },
          },
          scales: {
            x: { ticks: { color: tickColor(dk), font: { size: 11 } }, grid: { display: false } },
            y: {
              beginAtZero: true,
              ticks: { color: tickColor(dk), callback: (v) => fmtSeg(v) },
              grid: { color: gridColor(dk) },
            },
          },
        }}
      />
    </Box>
  );
};

/** Campañas – Horizontal Bar */
const ChartCampanias = ({ data, isDarkMode: dk }) => {
  if (!data || !data.labels || data.labels.length === 0) return null;
  const cfg = {
    labels: data.labels,
    datasets: [
      { label: 'Total', data: data.total, backgroundColor: '#3b82f6', borderRadius: 3 },
      { label: 'Atendidas', data: data.atendidas, backgroundColor: '#22c55e', borderRadius: 3 },
      { label: 'Abandonadas', data: data.abandonadas, backgroundColor: '#ef4444', borderRadius: 3 },
    ],
  };
  const h = Math.max(280, data.labels.length * 32);
  return (
    <Box sx={{ height: h, ...chartBox(dk) }}>
      <Bar
        data={cfg}
        options={{
          indexAxis: 'y',
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { position: 'top', labels: { color: tickColor(dk), boxWidth: 12, font: { size: 11 } } },
            datalabels: { display: false },
            tooltip: {
              ...ttStyle(dk),
              callbacks: {
                title: (items) => data.fullLabels?.[items[0]?.dataIndex] || items[0]?.label,
              },
            },
          },
          scales: {
            x: { beginAtZero: true, ticks: { color: tickColor(dk), precision: 0 }, grid: { color: gridColor(dk) } },
            y: { ticks: { color: tickColor(dk), font: { size: 10 } }, grid: { display: false } },
          },
        }}
      />
    </Box>
  );
};

/** Doughnut genérico */
const DoughnutChart = ({ entries, isDarkMode: dk, valueFormatter }) => {
  if (!entries || entries.length === 0) return null;
  const cfg = {
    labels: entries.map(([k]) => k),
    datasets: [
      {
        data: entries.map(([, v]) => v),
        backgroundColor: entries.map((_, i) => PALETTE[i % PALETTE.length]),
        borderWidth: 0,
      },
    ],
  };
  return (
    <Box sx={{ height: 260, display: 'flex', alignItems: 'center', justifyContent: 'center', ...chartBox(dk) }}>
      <Doughnut
        data={cfg}
        options={{
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { position: 'right', labels: { color: tickColor(dk), font: { size: 11 }, boxWidth: 12 } },
            datalabels: {
              color: '#fff',
              font: { size: 11, weight: 'bold' },
              formatter: valueFormatter || ((v) => v.toLocaleString('es-AR')),
            },
            tooltip: ttStyle(dk),
          },
        }}
      />
    </Box>
  );
};

/** Resultado de Gestión – Horizontal Bar */
const ChartResultadoGestion = ({ entries, isDarkMode: dk }) => {
  if (!entries || entries.length === 0) return null;
  const cfg = {
    labels: entries.map(([k]) => k.length > 30 ? k.slice(0, 27) + '...' : k),
    datasets: [
      {
        label: 'Interacciones',
        data: entries.map(([, v]) => v),
        backgroundColor: '#8b5cf6',
        borderRadius: 3,
      },
    ],
  };
  const h = Math.max(260, entries.length * 28);
  return (
    <Box sx={{ height: h, ...chartBox(dk) }}>
      <Bar
        data={cfg}
        options={{
          indexAxis: 'y',
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: false },
            datalabels: { display: false },
            tooltip: {
              ...ttStyle(dk),
              callbacks: {
                title: (items) => entries[items[0]?.dataIndex]?.[0] || items[0]?.label,
              },
            },
          },
          scales: {
            x: { beginAtZero: true, ticks: { color: tickColor(dk), precision: 0 }, grid: { color: gridColor(dk) } },
            y: { ticks: { color: tickColor(dk), font: { size: 10 } }, grid: { display: false } },
          },
        }}
      />
    </Box>
  );
};

/** Causa Terminación – Horizontal Bar */
const ChartCausaTerminacion = ({ entries, isDarkMode: dk }) => {
  if (!entries || entries.length === 0) return null;
  const cfg = {
    labels: entries.map(([k]) => k.length > 35 ? k.slice(0, 32) + '...' : k),
    datasets: [
      {
        label: 'Interacciones',
        data: entries.map(([, v]) => v),
        backgroundColor: '#06b6d4',
        borderRadius: 3,
      },
    ],
  };
  const h = Math.max(220, entries.length * 30);
  return (
    <Box sx={{ height: h, ...chartBox(dk) }}>
      <Bar
        data={cfg}
        options={{
          indexAxis: 'y',
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: false },
            datalabels: { display: false },
            tooltip: {
              ...ttStyle(dk),
              callbacks: {
                title: (items) => entries[items[0]?.dataIndex]?.[0] || items[0]?.label,
              },
            },
          },
          scales: {
            x: { beginAtZero: true, ticks: { color: tickColor(dk), precision: 0 }, grid: { color: gridColor(dk) } },
            y: { ticks: { color: tickColor(dk), font: { size: 10 } }, grid: { display: false } },
          },
        }}
      />
    </Box>
  );
};

/* ========================================================================= */
/*  DASHBOARD PRINCIPAL                                                      */
/* ========================================================================= */

const DashboardDetalleLlamadas = ({ rawRows }) => {
  const theme = useTheme();
  const dk = theme.palette.mode === 'dark';

  // Estado de filtros
  const [filters, setFilters] = useState({
    fechas: [],
    fechaDesde: '',
    fechaHasta: '',
    horaDesde: 0,
    horaHasta: 24,
    campanias: [],
    tipoContacto: [],
    sentido: [],
    causaTerminacion: [],
  });

  // Inicializar fechas cuando cambian los rawRows
  useEffect(() => {
    if (!rawRows || rawRows.length === 0) return;
    const fechasSet = new Set();
    rawRows.forEach((r) => {
      if (r.Inicio) {
        const d = new Date(r.Inicio);
        if (!isNaN(d)) fechasSet.add(d.toISOString().split('T')[0]);
      }
    });
    const sorted = [...fechasSet].sort();
    setFilters((prev) => ({
      ...prev,
      fechas: sorted,
      fechaDesde: sorted[0] || '',
      fechaHasta: sorted[sorted.length - 1] || '',
    }));
  }, [rawRows]);

  // Aplicar filtros locales
  const filteredRows = useMemo(() => aplicarFiltrosDetalle(rawRows, filters), [rawRows, filters]);
  const datos = useMemo(() => procesarDatosDetalleLlamadas(filteredRows), [filteredRows]);

  if (!datos) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
        <FilterBarDetalleLlamadas rawRows={rawRows} filters={filters} onChange={setFilters} />
        <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
          No hay datos para mostrar con los filtros actuales.
        </Typography>
      </Box>
    );
  }

  const { kpis } = datos;

  const kpiCards = [
    {
      title: 'Total Interacciones',
      value: kpis.totalInteracciones.toLocaleString('es-AR'),
      icon: PhoneInTalkIcon,
      color: '#3b82f6',
    },
    {
      title: 'Atendidas',
      value: kpis.totalAtendidas.toLocaleString('es-AR'),
      subtitle: `${kpis.pctAtendidas.toFixed(1)}% del total`,
      icon: CheckCircleOutlineIcon,
      color: '#22c55e',
    },
    {
      title: 'Abandonadas',
      value: kpis.totalAbandonadas.toLocaleString('es-AR'),
      icon: PhoneMissedIcon,
      color: '#ef4444',
    },
    {
      title: '% Atención',
      value: `${kpis.pctAtendidas.toFixed(1)}%`,
      icon: PercentIcon,
      color: '#8b5cf6',
    },
    {
      title: 'Dur. Promedio',
      value: fmtSeg(kpis.promDuracion),
      subtitle: `Total: ${fmtSeg(kpis.totalDuracion)}`,
      icon: TimerIcon,
      color: '#f59e0b',
    },
    {
      title: 'Talking Prom.',
      value: fmtSeg(kpis.promTalkingTime),
      subtitle: `ACW prom: ${fmtSeg(kpis.promACW)}`,
      icon: HeadsetMicIcon,
      color: '#ec4899',
    },
    {
      title: 'Espera Prom.',
      value: fmtSeg(kpis.promEnCola),
      subtitle: `Hold total: ${fmtSeg(kpis.totalHold)}`,
      icon: AccessTimeIcon,
      color: '#06b6d4',
    },
    {
      title: 'Agentes Únicos',
      value: kpis.agentesUnicos.toLocaleString('es-AR'),
      subtitle: `Transfer: ${kpis.totalTransferIn + kpis.totalTransferOut}`,
      icon: PeopleIcon,
      color: '#10b981',
    },
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
      {/* Filtros locales */}
      <FilterBarDetalleLlamadas rawRows={rawRows} filters={filters} onChange={setFilters} />

      {/* KPI Cards */}
      <Grid container spacing={1.5}>
        {kpiCards.map((c, i) => (
          <Grid item xs={6} sm={4} md={3} key={i}>
            <KpiCard {...c} isDarkMode={dk} />
          </Grid>
        ))}
      </Grid>

      <Divider />

      {/* Row 1: Por Fecha + Duración promedio por fecha */}
      <Grid container spacing={2}>
        <Grid item xs={12} lg={6}>
          <SectionTitle isDarkMode={dk}>Interacciones por Fecha</SectionTitle>
          <ChartPorFecha data={datos.chartPorFecha} isDarkMode={dk} />
        </Grid>
        <Grid item xs={12} lg={6}>
          <SectionTitle isDarkMode={dk}>Duración Promedio por Fecha</SectionTitle>
          <ChartDuracionPorFecha data={datos.chartPorFecha} isDarkMode={dk} />
        </Grid>
      </Grid>

      {/* Row 2: Por Hora + Tipo Contacto + Sentido */}
      <Grid container spacing={2}>
        <Grid item xs={12} lg={6}>
          <SectionTitle isDarkMode={dk}>Distribución por Hora del Día</SectionTitle>
          <ChartPorHora data={datos.chartPorHora} isDarkMode={dk} />
        </Grid>
        <Grid item xs={12} md={3}>
          <SectionTitle isDarkMode={dk}>Tipo de Contacto</SectionTitle>
          <DoughnutChart entries={datos.tipoContacto} isDarkMode={dk} />
        </Grid>
        <Grid item xs={12} md={3}>
          <SectionTitle isDarkMode={dk}>Sentido</SectionTitle>
          <DoughnutChart entries={datos.sentido} isDarkMode={dk} />
        </Grid>
      </Grid>

      {/* Row 3: Campañas (full width) */}
      <Box>
        <SectionTitle isDarkMode={dk}>Interacciones por Campaña</SectionTitle>
        <ChartCampanias data={datos.chartCampanias} isDarkMode={dk} />
      </Box>

      {/* Row 4: Causa Terminación + Resultado Gestión */}
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <SectionTitle isDarkMode={dk}>Causa de Terminación</SectionTitle>
          <ChartCausaTerminacion entries={datos.causaTerminacion} isDarkMode={dk} />
        </Grid>
        <Grid item xs={12} md={6}>
          <SectionTitle isDarkMode={dk}>Resultado de Gestión (Top 15)</SectionTitle>
          <ChartResultadoGestion entries={datos.resultadoGestion} isDarkMode={dk} />
        </Grid>
      </Grid>
    </Box>
  );
};

export default DashboardDetalleLlamadas;
